import { useCallback, useEffect } from 'react'
import moment from 'moment-timezone'

import { CompensationInfo } from 'app/components/Cafeteria/Compensation/CompensationInfo'
import { Statements } from 'app/components/Cafeteria/Compensation/Statements'
import { WithdrawToParent } from 'app/components/Cafeteria/Compensation/WithdrawToParent'
import { SupplierSelector } from 'app/components/Cafeteria/Compensation/SupplierSelector'
import { Space } from 'app/common/templates-next/report-template'
import { useCompensationEnabledByStudent } from 'app/dataProviders/user/hooks'

import {
  getStudentIdExact,
  getSchoolTypeExact,
  getSupplierId,
  getPeriodFrom,
  getPeriodTo,
  getSection,
  params as router,
  createPath,
  cafeteriaCompensationPathMask,
  dateFormat,
  getWithdrawId,
  cafeteriaBasePathMask,
} from 'app/pages/routePaths'

const getDefaultPeriodFrom = () => {
  const mayBe = moment().set('month', 8).set('date', 1)
  return (mayBe.isAfter(moment()) ? mayBe.subtract(1, 'year') : mayBe).format(dateFormat)
}

const getDefaultPeriodTo = () => {
  return moment().format(dateFormat)
}

enum Section {
  Root = 'root',
  SelectSupplier = 'select-supplier',
  WParent = 'withdraw-to-parent',
  WChild = 'withdraw-to-child',
}

type Props = {
  params: any
  route: any
  router: {
    push: (path: string) => void
    replace: (path: string) => void
  }
}

export const CompensationPage = ({ params, route, router: { push, replace } }: Props) => {
  const studentID = getStudentIdExact(params)
  const schoolType = getSchoolTypeExact(params)
  const supplierID = getSupplierId(params) ?? null
  const section = getSection(params)
  const periodFrom = getPeriodFrom(params)
  const periodTo = getPeriodTo(params)
  const withdrawID = getWithdrawId(params) ?? null

  const { compensationByStatement, compensationByCabinet } =
    useCompensationEnabledByStudent(studentID)

  useEffect(() => {
    if (!compensationByStatement && !compensationByCabinet) {
      replace(
        createPath(
          {
            [router.studentID]: studentID,
            [router.schoolType]: schoolType,
          },
          cafeteriaBasePathMask,
        ),
      )
    }
  }, [compensationByStatement, compensationByCabinet, studentID, schoolType, replace])

  const rootURL = createPath(
    {
      [router.studentID]: studentID,
      [router.schoolType]: schoolType,
      [router.section]: Section.Root,
      [router.supplierID]: supplierID,
      [router.periodFrom]: periodFrom,
      [router.periodTo]: periodTo,
    },
    cafeteriaCompensationPathMask,
  )

  const returnToRoot = useCallback(() => {
    push(rootURL)
  }, [rootURL, push])

  const supplierSelectorURL = createPath(
    {
      [router.studentID]: studentID,
      [router.schoolType]: schoolType,
      [router.section]: Section.SelectSupplier,
      [router.supplierID]: supplierID,
    },
    cafeteriaCompensationPathMask,
  )

  const withdrawToParentURL = createPath(
    {
      [router.studentID]: studentID,
      [router.schoolType]: schoolType,
      [router.section]: Section.WParent,
      [router.supplierID]: supplierID,
      [router.periodFrom]: periodFrom || getDefaultPeriodFrom(),
      [router.periodTo]: periodTo || getDefaultPeriodTo(),
    },
    cafeteriaCompensationPathMask,
  )

  const changeSupplier = useCallback(
    (newSupplierID: string) => {
      const pathWithNewSupplier = createPath(
        {
          [router.studentID]: studentID,
          [router.schoolType]: schoolType,
          [router.section]: Section.Root,
          [router.supplierID]: newSupplierID,
          [router.periodFrom]: periodFrom || getDefaultPeriodFrom(),
          [router.periodTo]: periodTo || getDefaultPeriodTo(),
        },
        cafeteriaCompensationPathMask,
      )
      push(pathWithNewSupplier)
    },
    [studentID, schoolType, periodFrom, periodTo, push],
  )

  const changePeriod = useCallback(
    (from: string | null, to: string | null, format: string) => {
      const pathWithNewSupplier = createPath(
        {
          [router.studentID]: studentID,
          [router.schoolType]: schoolType,
          [router.section]: Section.Root,
          [router.supplierID]: supplierID,
          [router.periodFrom]: from ? moment(from, format).format(dateFormat) : undefined,
          [router.periodTo]: to ? moment(to, format).format(dateFormat) : undefined,
        },
        cafeteriaCompensationPathMask,
      )
      push(pathWithNewSupplier)
    },
    [studentID, schoolType, supplierID, push],
  )

  const withdrawToParent = useCallback(
    (withdrawID_: string) => {
      const pathWithdrawToParent = createPath(
        {
          [router.studentID]: studentID,
          [router.schoolType]: schoolType,
          [router.section]: Section.WParent,
          [router.supplierID]: supplierID,
          [router.periodFrom]: periodFrom || getDefaultPeriodFrom(),
          [router.periodTo]: periodTo || getDefaultPeriodTo(),
          [router.withdrawID]: withdrawID_,
        },
        cafeteriaCompensationPathMask,
      )
      push(pathWithdrawToParent)
    },
    [studentID, schoolType, periodFrom, periodTo, supplierID, push],
  )

  useEffect(() => {
    if (supplierID && (!periodFrom || !periodTo)) {
      changePeriod(getDefaultPeriodFrom(), getDefaultPeriodTo(), dateFormat)
    }
  }, [changePeriod]) // eslint-disable-line react-hooks/exhaustive-deps

  if (section === Section.SelectSupplier) {
    return (
      <SupplierSelector
        studentID={studentID}
        supplierID={supplierID}
        returnLink={rootURL}
        onChangeSupplier={changeSupplier}
      />
    )
  }

  if (section === Section.WParent && supplierID) {
    return (
      <WithdrawToParent
        studentID={studentID}
        supplierID={supplierID}
        withdrawID={withdrawID}
        returnToRoot={returnToRoot}
        route={route}
      />
    )
  }

  return (
    <>
      <CompensationInfo
        studentID={studentID}
        supplierID={supplierID}
        supplierSelectorLink={supplierSelectorURL}
        withdrawToChildrenLink=""
        withdrawToParentLink={withdrawToParentURL}
        returnLink={rootURL}
        onChangeSupplier={changeSupplier}
      />
      <Space />
      <Statements
        studentID={studentID}
        supplierID={supplierID || null}
        periodFrom={periodFrom || null}
        periodTo={periodTo || null}
        periodFormat={dateFormat}
        onRedirectPeriod={changePeriod}
        onRedirectWithdrawToParent={withdrawToParent}
      />
    </>
  )
}
